import { useMemo } from 'react';
import { NormalizedTask, qualityFlags, QUALITY_LABELS, QualityKey } from '../../domain/normalize';
import { UserDoc, STATUSES, URGENCIAS, DIFICULTADES, Dimension } from '../../domain/types';
import { nameByEmail } from '../../domain/util';
import { Card, Kpi, BarCard, ExportButton, BoardIntro } from './shared';

const KNOWN_STATUS = new Set<string>(STATUSES);
const KNOWN_URG = new Set<string>([...URGENCIAS, '']);
const KNOWN_DIF = new Set<string>([...DIFICULTADES, '']);

export default function BoardCalidad({ tasks, users, dimensions }: {
  tasks: NormalizedTask[]; users: UserDoc[]; dimensions: Record<string, Dimension[]>;
}) {
  const knownCat = useMemo(() => new Set((dimensions.categoria || []).map((d) => d.name)), [dimensions]);
  const knownEmail = useMemo(() => new Set(users.map((u) => u.email.toLowerCase())), [users]);

  // Flags por tarea
  const flagged = useMemo(
    () => tasks.map((t) => ({ task: t, flags: qualityFlags(t.raw) })).filter((x) => x.flags.length),
    [tasks]
  );

  const counts = useMemo(() => {
    const m = new Map<QualityKey, number>();
    flagged.forEach((x) => x.flags.forEach((f) => m.set(f, (m.get(f) || 0) + 1)));
    return (Object.keys(QUALITY_LABELS) as QualityKey[]).map((k) => ({ name: QUALITY_LABELS[k], value: m.get(k) || 0 })).sort((a, b) => b.value - a.value);
  }, [flagged]);

  // Valores no mapeados contra catálogos/enums reales
  const noMapeados = useMemo(() => {
    const out: { tipo: string; valor: string; count: number }[] = [];
    const add = (tipo: string, valor: string, map: Map<string, number>) => map.set(`${tipo}::${valor}`, (map.get(`${tipo}::${valor}`) || 0) + 1);
    const m = new Map<string, number>();
    tasks.forEach((t) => {
      if (t.estado && !KNOWN_STATUS.has(t.estado)) add('Estado', t.estado, m);
      if (t.urgencia && !KNOWN_URG.has(t.urgencia)) add('Urgencia', t.urgencia, m);
      if (t.dificultad && !KNOWN_DIF.has(t.dificultad)) add('Dificultad', t.dificultad, m);
      if (t.area_produccion && knownCat.size && !knownCat.has(t.area_produccion)) add('Categoría', t.area_produccion, m);
      if (t.responsable && !knownEmail.has(t.responsable.toLowerCase())) add('Responsable', t.responsable, m);
    });
    m.forEach((count, key) => { const [tipo, valor] = key.split('::'); out.push({ tipo, valor, count }); });
    return out.sort((a, b) => b.count - a.count);
  }, [tasks, knownCat, knownEmail]);

  const csvRows = flagged.map((x) => ({
    id: x.task.id, descripcion: x.task.descripcion, responsable: nameByEmail(x.task.responsable, users),
    problemas: x.flags.map((f) => QUALITY_LABELS[f]).join(' | '),
  }));

  const totalConProblemas = flagged.length;
  const pctLimpio = tasks.length ? Math.round(((tasks.length - totalConProblemas) / tasks.length) * 100) : 100;

  return (
    <div className="grid">
      <BoardIntro
        titulo="Calidad de los datos"
        objetivo="Detecta tareas mal cargadas (sin responsable, sin fecha, sin horas) que impiden que los demás tableros midan bien. Pensado para quien administra la carga."
        preguntas={['¿Qué tareas están incompletas?', '¿Hay datos cargados con valores raros?', '¿Podemos confiar en los números?']}
      />
      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))' }}>
        <Kpi label="Tareas con problemas" value={String(totalConProblemas)} tone={totalConProblemas ? 'warn' : 'ok'} help="Tareas a las que les falta algún dato importante." />
        <Kpi label="% registros limpios" value={`${pctLimpio}%`} tone={pctLimpio >= 90 ? 'ok' : pctLimpio < 70 ? 'bad' : 'warn'} help="Porcentaje de tareas sin ningún problema de carga. Cuanto más alto, más confiables son los tableros." />
        <Kpi label="Valores no mapeados" value={String(noMapeados.length)} tone={noMapeados.length ? 'bad' : 'ok'} help="Valores cargados que no existen en las listas oficiales (estados, categorías, personas)." />
      </div>

      <BarCard title="Errores de carga por tipo" data={counts} color="#b91c1c" horizontal />

      <Card title="Valores no mapeados (contra enums y catálogos reales)" hint="Estados, urgencia, dificultad, categoría o responsable que no existen en la taxonomía.">
        {noMapeados.length === 0 ? (
          <p className="muted">Sin valores fuera de catálogo. ✓</p>
        ) : (
          <table>
            <thead><tr><th>Tipo</th><th>Valor</th><th className="num">Apariciones</th></tr></thead>
            <tbody>{noMapeados.map((r, i) => <tr key={i}><td>{r.tipo}</td><td>{r.valor}</td><td className="num">{r.count}</td></tr>)}</tbody>
          </table>
        )}
      </Card>

      <Card title="Tareas con datos faltantes" action={<ExportButton filename="calidad_datos.csv" rows={csvRows} />}>
        <div style={{ overflowX: 'auto', maxHeight: 420 }}>
          <table>
            <thead><tr><th>Descripción</th><th>Responsable</th><th>Problemas detectados</th></tr></thead>
            <tbody>
              {flagged.map((x) => (
                <tr key={x.task.id}>
                  <td>{x.task.descripcion.slice(0, 50)}</td>
                  <td>{x.task.responsable ? nameByEmail(x.task.responsable, users) : <em>sin asignar</em>}</td>
                  <td>{x.flags.map((f) => <span key={f} className="badge" style={{ background: '#fef3c7', color: '#92400e', marginRight: 4 }}>{QUALITY_LABELS[f]}</span>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
